import { useState } from 'react'
import { Info } from 'lucide-react'

import type { ImpactResponse } from '@/api/teamInsights'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePricing } from '@/hooks/usePricing'
import { cn } from '@/lib/utils'

const SALARY_MIN = 20
const SALARY_MAX = 300
const SALARY_DEFAULT = 100

// Native <input type="range"> needs vendor-prefixed pseudo-elements to style the thumb, split into named constants for readability.
const SLIDER_BASE_CLASSES =
  'h-2 w-full cursor-pointer appearance-none rounded-full outline-none ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fathom)]'
const SLIDER_MOZ_THUMB_CLASSES =
  '[&::-moz-range-thumb]:size-5 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none ' +
  '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[2.45px] [&::-moz-range-thumb]:border-white ' +
  '[&::-moz-range-thumb]:bg-[var(--fathom)] [&::-moz-range-thumb]:shadow-md'
const SLIDER_WEBKIT_THUMB_CLASSES =
  '[&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none ' +
  '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[2.45px] [&::-webkit-slider-thumb]:border-white ' +
  '[&::-webkit-slider-thumb]:bg-[var(--fathom)] [&::-webkit-slider-thumb]:shadow-md'
const SLIDER_CLASSES = cn(SLIDER_BASE_CLASSES, SLIDER_MOZ_THUMB_CLASSES, SLIDER_WEBKIT_THUMB_CLASSES)

interface MoneySavedCardProps {
  valueCalculator: ImpactResponse['value_calculator']
}

const MoneySavedCard = ({ valueCalculator }: MoneySavedCardProps) => {
  const [salaryRate, setSalaryRate] = useState<number>(SALARY_DEFAULT)
  const { formatPrice, localizeNumber, locale: ipLocale } = usePricing()

  // Lock the whole card to the enterprise's billing currency when known;
  // otherwise fall back to the IP-derived currency in `formatPrice`.
  // Stripe ships lowercase ISO codes; Intl wants upper.
  const billingCurrency = valueCalculator.currency?.toUpperCase() ?? null
  const cardLocale = ipLocale || navigator.language

  const decimalsForCurrency = (currency: string): number =>
    new Intl.NumberFormat(cardLocale, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2

  const formatInBillingCurrency = (amount: number, opts?: { compact?: boolean }): string => {
    if (!billingCurrency) return formatPrice(amount, opts)
    const hasFraction = amount % 1 !== 0
    return new Intl.NumberFormat(cardLocale, {
      style: 'currency',
      currency: billingCurrency,
      ...(opts?.compact
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : {
            minimumFractionDigits: hasFraction ? 2 : 0,
            maximumFractionDigits: hasFraction ? 2 : 0,
          }),
    }).format(amount)
  }

  const totalMinutesPerMonth = Math.round(valueCalculator.monthly_minutes_saved)
  const valueSavedPerMonth = (valueCalculator.monthly_minutes_saved / 60) * salaryRate

  // Backend ships Stripe minor units; derive the divisor from Intl so JPY/KRW
  // (zero-decimal) work without a hardcoded currency list.
  const monthlyCost =
    valueCalculator.monthly_cost_minor_units != null && billingCurrency
      ? valueCalculator.monthly_cost_minor_units / 10 ** decimalsForCurrency(billingCurrency)
      : null

  // Floor at 0 — never show negative ROI.
  const netSavedPerMonth = monthlyCost == null ? valueSavedPerMonth : Math.max(0, valueSavedPerMonth - monthlyCost)
  // When cost is unknown, the headline is gross value (not net ROI) — drop the
  // "+" so the same number doesn't silently mean two different things.
  const headlinePrefix = monthlyCost == null ? '' : '+'

  return (
    <Card className="border-[var(--fathom-100)] bg-[var(--fathom-50)]/20 pt-0">
      <CardHeader className="flex items-center gap-4 space-y-0 rounded-t-xl border-b border-[var(--fathom-100)] bg-[var(--fathom-50)] pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-0.5">
          <CardTitle className="text-xl font-semibold">ROI</CardTitle>
          <p className="text-[15px] leading-5 text-muted-foreground">Explore how much Flow is saving your team</p>
        </div>
        <span className="inline-flex items-center rounded-md bg-[var(--fathom)] px-2.5 py-1 text-[12px] font-medium leading-none text-white">
          Interactive tool
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-8 px-6 pb-8 pt-8">
        <div className="text-center text-[56px] font-semibold leading-none tracking-tight text-foreground">
          {headlinePrefix}
          {formatInBillingCurrency(Math.round(netSavedPerMonth), {
            compact: netSavedPerMonth >= 100_000,
          })}
          /month
        </div>
        <dl className="grid w-full grid-cols-[1fr_auto] gap-x-6 gap-y-3 text-[15px] leading-5">
          <dt className="font-normal">Team members</dt>
          <dd className="text-right font-semibold">{localizeNumber(valueCalculator.team_member_count)}</dd>
          <dt className="flex items-center gap-1.5 font-normal">
            Total minutes saved per month
            <Tooltip>
              <TooltipTrigger
                aria-label="How minutes saved is calculated"
                className="inline-flex items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fathom)]"
              >
                <Info className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[320px] text-muted-foreground">
                <p className="mb-2">How we calculate this:</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>Your team&apos;s average WPM using Flow</li>
                  <li>Total words dictated</li>
                  <li>Compared to a 40 WPM typing baseline</li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </dt>
          <dd className="text-right font-semibold">{localizeNumber(totalMinutesPerMonth)} min</dd>
          <dt className="font-normal">Value saved per month</dt>
          <dd className="text-right font-semibold">{formatInBillingCurrency(Math.round(valueSavedPerMonth))}</dd>
          <dt className="font-normal">Flow team cost per month</dt>
          <dd className="text-right font-semibold">
            {monthlyCost === null ? '—' : formatInBillingCurrency(Math.round(monthlyCost))}
          </dd>
        </dl>
        <div className="flex w-full flex-col gap-3">
          <p className="text-[20px] font-semibold leading-7 text-[var(--fathom)]">
            At a salary rate of <span className="underline">{formatInBillingCurrency(salaryRate)}/hr</span>
          </p>
          <input
            type="range"
            min={SALARY_MIN}
            max={SALARY_MAX}
            step={5}
            value={salaryRate}
            onChange={(e) => setSalaryRate(Number(e.target.value))}
            aria-label="Salary rate per hour"
            className={SLIDER_CLASSES}
            style={{
              ['--track-fill' as string]: `${((salaryRate - SALARY_MIN) / (SALARY_MAX - SALARY_MIN)) * 100}%`,
              background:
                'linear-gradient(to right, var(--fathom) 0%, var(--fathom) var(--track-fill, 33%), var(--sand-600) var(--track-fill, 33%), var(--sand-600) 100%)',
            }}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export default MoneySavedCard
